import React, { useMemo, useState } from 'react';
import { Form, Formik, useFormik } from 'formik';
import * as Yup from 'yup';
import { Button } from '../Button/Button';
import { Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap';
import { decamelizeKeys } from 'humps';
import { getDeliverableRouteOrders, getRouteColor } from '../../utils/utils';
import { format } from 'date-fns';
import { Status } from '../Status';
import { ServiceBadges } from '../TimeLine/ServiceBadges';

export const RouteDelayModal = ({ route, routeOrders, onModalClose, isOpen }) => {
  const [loading, setLoading] = useState();

  // Driver breaks and buffers have no `order` — rendering them here would throw
  // and unmount the whole page (869dxv1b8).
  const notifiableRouteOrders = useMemo(
    () => getDeliverableRouteOrders(routeOrders),
    [routeOrders]
  );

  const formik = useFormik({
    initialValues: {
      selectedOrders: [],
    },
    validationSchema: Yup.object({
      selectedOrders: Yup.array().of(
        Yup.number()
          .integer('Each element must be an integer')
          .required('Each element is required')
      ).min(1),
    }),
    onSubmit: async (values, formik) => {
      const result = { ...values };
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content');

      try {
        setLoading(true);

        const response = await fetch(`/admin/routes/${route.id}/delays`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
          },
          body: JSON.stringify(decamelizeKeys(result)),
        });

        // fetch() only rejects on network failure — 4xx/5xx must be raised by hand,
        // otherwise a failed send still reports success.
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        formik.resetForm();
        toastr.success(__('routes.modal.delay_info_success'));
        onModalClose();
      } catch (error) {
        toastr.error(__('routes.modal.delay_info_error'));
      } finally {
        setLoading(false);
      }
    },
  });

  const handleOrdersChange = (id) => {
    const selectedOrders = formik.values.selectedOrders;

    if (selectedOrders.includes(id)) {
      formik.setFieldValue(
        'selectedOrders',
        selectedOrders.filter((orderId) => orderId !== id)
      );
    } else {
      formik.setFieldValue('selectedOrders', [...selectedOrders, id]);
    }
  };

  const handleSelectAllOrders = () => {
    if (formik.values.selectedOrders.length === notifiableRouteOrders.length) {
      formik.setFieldValue('selectedOrders', []);
    } else {
      const allOrderIds = notifiableRouteOrders.map((routeOrder) => routeOrder.order.id);
      formik.setFieldValue('selectedOrders', allOrderIds);
    }
  };

  const handleClose = () => {
    formik.resetForm();
    onModalClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      className="modal-xl max-90-vh"
      contentClassName="max-90-vh"
    >
      <ModalHeader toggle={handleClose}>{__('routes.modal.notify_delay_info')}</ModalHeader>
      <ModalBody className='overflow-scroll'>
        <Formik>
          {(index) => (
            <Form key={index} className="row">
              <div className="col-12 overflow-auto">
                <div className="col-12">
                  <ul className="list-unstyled">
                    {notifiableRouteOrders.map((routeOrder, index) => {
                      const {
                        order,
                        stage,
                        unloadingTime,
                        arrivalDatetime,
                        distance,
                        speed,
                        editable,
                        calculatedArrivalTime,
                        statuses,
                        onStatusChange = () => { },
                      } = routeOrder;

                      const {
                        id,
                        trackingId,
                        sender,
                        parcelDescription,
                        deliveryPostcode,
                        deliveryCity,
                        deliveryStreet,
                        deliveryHouse,
                        deliveryPhone,
                        deliveryFirstName,
                        deliveryLastName,
                        deliveryLocale,
                      } = order || {};

                      return (
                        <li
                          key={routeOrder.id}
                          className="timeline-item timeline-icon-primary active d-flex flex-row mb-2"
                        >
                        <div className='d-flex align-items-center'>
                          <fieldset>
                            <div className="checkbox checkbox-primary">
                              <input type="checkbox"
                                     className="checkbox-input"
                                     name="orders"
                                     id={`checkbox-order-${routeOrder.order.id}`}
                                     value={String(routeOrder.order.id)}
                                     checked={formik.values.selectedOrders.includes(order.id)}
                                     onChange={() => handleOrdersChange(routeOrder.order.id)}
                              />
                              <label htmlFor={`checkbox-order-${routeOrder.order.id}`}></label>
                            </div>
                          </fieldset>
                        </div>
                        <div className="w-100">
                          <div className="timeline-item-label col-2" style={{ backgroundColor: getRouteColor(stage) }}>
                            <p className="tx-07 lp mb-0 lh-1 opa-05">{('0' + (index + 1)).slice(-2)}</p>
                            <p className="tx-08 text-bold-700 mb-0 lh-1">{format(arrivalDatetime, 'HH:mm')}</p>
                            {calculatedArrivalTime && <p className="tx-07 lp mb-0 lh-1 opa-05">{calculatedArrivalTime}</p>}
                          </div>
                          <div className="timeline-info timeline-info--with-border" style={{ borderColor: getRouteColor(stage) }}>
                            <div className="row col-12 p-0 m-0">
                              <div className="col-4 pt-1 pb-1 border-right">
                                <h1 className="text-primary tx-15 text-bold-700">
                                  {trackingId}
                                </h1>
                                <p className="text-bold-700 tx-08 mb-1 mt-1 lh-1 pt-2px">{parcelDescription}</p>
                                {!editable && (
                                  <>
                                    <p className="tx-08 mb-0 lh-1">Nadawca: {sender?.name}</p>
                                  </>
                                )}
                                <p className="tx-08 mb-0 lh-1">Il. paczek: {order.packages?.length ?? 0}</p>
                              </div>
                              <div className="col-4 pt-1 pb-1 border-right">
                                <p className="text-bold-700 tx-1 mb-0">
                                  {deliveryPostcode}, <span className="text-bold-400 tx-08 mb-0">{deliveryCity}</span>
                                </p>
                                <p className="tx-08 mb-0 lh-1">
                                  {deliveryStreet} {deliveryHouse}
                                </p>
                                <p className="tx-1 mt-1 mb-5px">
                                  {`${deliveryFirstName} ${deliveryLastName}`}
                                </p>
                                <p className="tx-09 mb-5px">
                                  <span className="tx-08 mb-0 lh-1">
                                      Tel.:
                                  </span>
                                  <span className="text-bold-700 tx-08 mb-0 lh-1">
                                      {deliveryPhone}
                                  </span>
                                </p>
                              </div>
                              <div className='col-2 pt-1 pb-1'>
                                <Status order={order} editable={!editable} statuses={statuses} onStatusChange={onStatusChange} />
                                {order?.orderServices?.length > 0 && <>
                                  <p className='text-muted tx-07 mt-8px mb-0'>Usługi</p>
                                  <ServiceBadges services={order.orderServices} />
                                </>}
                                <p className='text-muted tx-07 mt-1 mb-0'>{__('routes.unloading_time')}</p>
                                <div className='tx-08'>{unloadingTime} min</div>
                              </div>
                              <div className="col-2 pt-1 pb-1 border-left">
                                <p className="locale-label tx-08 mb-1">{deliveryLocale}</p>
                                <p className="text-muted tx-07 mb-0 lh-1">Data</p>
                                <p className="tx-08 mb-0 lh-1">
                                  {format(arrivalDatetime, 'dd-MM-yyyy')}
                                  <br />
                                  <br />
                                </p>
                                <p className="text-muted tx-07 mb-0 lh-1">Odcinek</p>
                                <p className="tx-08 mb-0 lh-1">{distance} km</p>
                                {speed !== undefined && <p className="tx-08 mb-0 lh-1">{speed} km/h</p>}
                                {order.media?.length > 0 && (
                                  <p className="tx-08 mt-1 lh-1 text-success">
                                    <i className='bx bx-edit font-size-base'></i> Odbiorca złożył podpis
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                      )
                    })}
                  </ul>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </ModalBody>
      <ModalFooter>
        <div className='d-flex w-100 justify-content-between'>
          <Button
            type="button"
            className="btn btn-secondary"
            onClick={handleSelectAllOrders}
          >
            {formik.values.selectedOrders.length === notifiableRouteOrders.length ? __('routes.modal.unselect_all') : __('routes.modal.select_all')}
          </Button>

          <div>
            <Button
              className="btn-secondary mr-1"
              onClick={handleClose}
            >
              {__('app.back')}
            </Button>

            <Button
              type="submit"
              className="btn-primary"
              onClick={formik.submitForm}
              disabled={loading || formik.values.selectedOrders.length === 0}
            >
              {__('app.send')}
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};
